import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import { addToScope, selectPartnersType } from 'legacy/lut/features/partner360/composedReport'
import { selectComposedReportFYear } from 'legacy/lut/features/partner360/composedReport/selectors/selectComposedReportFYear'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { IFilter, Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import { TSimpleTableFilterKeys } from 'partner360/components/simplePartnerTable/models'

import { extraColumns, globalBaseColumns } from '../configs'

interface IUsePartnersSelect {
  onClose: () => void
  isGlobal?: boolean
  onRowSelect?: (item: IWWPartners) => void
  uniqSourceKey?: string
}

export const usePartnersSelect = ({
  onClose,
  isGlobal,
  onRowSelect,
  uniqSourceKey,
}: IUsePartnersSelect) => {
  const dispatch = useDispatch()
  const [checkedPartners, setCheckedPartners] = useState<IWWPartners[]>([])
  const partnersType = useSelector(selectPartnersType)
  const composedReportFYear = useSelector(selectComposedReportFYear)

  const source = useMemo(() => {
    const crud = CrudStore.get(
      CrudStoreItemPartner360.JBPComposedReportPartnersScope,
      `composedReport-table-isGlobal=${uniqSourceKey}`
    )
    const filters: IFilter[] = [
      {
        field: 'RTM',
        operator: Operator.EQUAL,
        value: partnersType,
      },
      {
        field: 'FYear',
        operator: Operator.EQUAL,
        value: composedReportFYear,
      },
    ]

    if (isGlobal) {
      filters.push({
        field: 'IsGlobal',
        operator: Operator.EQUAL,
        value: 1,
      })
    }
    crud.setDefaultFilter({
      logic: Logic.AND,
      filters: filters,
    })

    return crud
  }, [composedReportFYear, isGlobal, partnersType, uniqSourceKey])

  const onAddPartnerToScope = useCallback(
    (item: IWWPartners) => {
      if (onRowSelect) {
        onRowSelect(item)
        return
      }
      dispatch(addToScope([item]))
      onClose()
    },
    [onRowSelect, dispatch, onClose]
  )

  const onPartnerCheck = useCallback((item: IWWPartners) => {
    setCheckedPartners((state) => {
      const found = state.find((el) => el.PartnerID === item.PartnerID)
      if (found) {
        return state.filter((el) => el.PartnerID !== found.PartnerID)
      }
      return [...state, item]
    })
  }, [])

  const onClearAllCheckedPartners = useCallback(() => {
    setCheckedPartners([])
  }, [])

  const onBulkAddPartnersToScope = useCallback(() => {
    dispatch(addToScope(checkedPartners))
    onClearAllCheckedPartners()
    onClose()
  }, [checkedPartners, onClearAllCheckedPartners, onClose, dispatch])

  const partnersListProps = useMemo(() => {
    return {
      onSelect: onAddPartnerToScope,
      sourceKey: 'composed-report-partners-scope-list',
      useRowCheckbox: !isGlobal,
      checkedValues: checkedPartners.map((el) => ({
        key: el.PartnerID,
        value: 'full' as 'full' | 'empty' | 'indeterminate',
      })),
      onCheck: onPartnerCheck,
      bulkSave: onBulkAddPartnersToScope,
      bulkClear: onClearAllCheckedPartners,
      withFilterArea: true,
      source,
      extraColumns: isGlobal ? undefined : extraColumns,
      baseColumns: isGlobal ? globalBaseColumns : undefined,
      usedFilters: isGlobal
        ? (['partnerId', 'partnerName'] as TSimpleTableFilterKeys[])
        : undefined,
    }
  }, [
    onAddPartnerToScope,
    checkedPartners,
    onPartnerCheck,
    onBulkAddPartnersToScope,
    onClearAllCheckedPartners,
    source,
    isGlobal,
  ])

  return {
    partnersListProps,
  }
}
